// <purpose>THE state table of the Auto button (picked 2026-10-31, clickable mock v2 + the adopted
// missing-pieces sheet): for each page, what one press will do right now — the announcement line's
// verb and its bold part, the page icon the button wears, whether a press acts, and the hover the
// QUIET button carries. One pure decision per page, so the line, the button's accessible name and
// the click can never tell the clerk three stories.</purpose>
// <dependencies>None. Pure — no React, no api — so it is testable as a table (autoAction.test.ts).</dependencies>
// <constraints>
//   - FIXED SENTENCES per state, with singular and plural spelled out. Never assemble grammar from
//     parts: „1 ausgewähltes Ticket" / „3 ausgewählte Tickets" / „Formularzeilen von 1 ausgewähltem
//     Vorgang" each exist as a whole sentence below.
//   - scope 'rule' = what the page offers with nothing picked (older tickets, all uploads, every
//     form in view); scope 'pick' = the clerk's selection, 0 included. A change of verb or scope
//     replays the line's entrance; a count changing inside one scope only updates the words.
//   - Selection mode with nothing ticked never falls back to the page's group: the line says „0
//     ausgewählte …" and the button is quiet. The clerk chose to pick; a press that then archived
//     the older-tickets group would be a different act than the one on screen.
//   - The ONE exception to „no hover while the line shows": the Excel lever with nothing ticked
//     when „all" cannot be offered — the line falls back to „0 ausgewählte Vorgänge" and the quiet
//     button's hover gives the reason (adopted 2026-10-31). The reason strings are
//     describeExcelBatchAction's refusals, unchanged.
// </constraints>

export type AutoIconKey = 'lightning' | 'archive' | 'restore' | 'download' | 'mass-download';
export type AutoScope = 'rule' | 'pick';

export interface AutoAction {
  icon: AutoIconKey;
  /** The line's verb, or null = idle: no line at all. */
  verb: string | null;
  /** The line's bold part. Non-null exactly when `verb` is. */
  what: string | null;
  scope: AutoScope | null;
  /** A press does something. False = the quiet button. */
  canAct: boolean;
  /** The line carries a × that clears the selection (a pick with at least one ticked). */
  clearable: boolean;
  /** The quiet button's hover: the next step, or why „all" is not offered. */
  hint: string | null;
}

/** The sentence the button's accessible name and the line share. */
export const autoSentence = (a: AutoAction): string => (a.verb ? `${a.verb}: ${a.what}` : a.hint ?? '');

const idle = (icon: AutoIconKey, hint: string): AutoAction =>
  ({ icon, verb: null, what: null, scope: null, canAct: false, clearable: false, hint });
const rule = (icon: AutoIconKey, verb: string, what: string): AutoAction =>
  ({ icon, verb, what, scope: 'rule', canAct: true, clearable: false, hint: null });
const pick = (icon: AutoIconKey, verb: string, n: number, what: string, hint: string | null = null): AutoAction =>
  ({ icon, verb, what, scope: 'pick', canAct: n > 0, clearable: n > 0, hint: n > 0 ? null : hint });

// ─── The fixed sentences ────────────────────────────────────────────────────────────────────────
export const pickedTickets = (n: number): string => (n === 1 ? '1 ausgewähltes Ticket' : `${n} ausgewählte Tickets`);
export const pickedUploads = (n: number): string => (n === 1 ? '1 ausgewählter Upload' : `${n} ausgewählte Uploads`);
export const allUploads = (n: number): string => (n === 1 ? '1 Upload' : `alle ${n} Uploads`);
export const pickedForms = (n: number): string => {
  if (n === 0) return '0 ausgewählte Vorgänge';
  return n === 1 ? 'Formularzeilen von 1 ausgewähltem Vorgang' : `Formularzeilen von ${n} ausgewählten Vorgängen`;
};
export const allForms = (n: number): string => (n === 1 ? 'Formularzeilen von 1 Vorgang' : `Formularzeilen aller ${n} Vorgänge`);
/** While the counts refetch the number is the previous view's: promise the view, not a figure. */
export const VIEW_FORMS = 'Formularzeilen der aktuellen Ansicht';
export const olderThan = (n: number, days: number): string =>
  `${n} ${n === 1 ? 'Ticket' : 'Tickets'} älter als ${days} ${days === 1 ? 'Tag' : 'Tage'}`;
export const decidedOver = (n: number, days: number): string =>
  `${n} ${n === 1 ? 'Ticket' : 'Tickets'} seit über ${days} ${days === 1 ? 'Tag' : 'Tagen'} entschieden`;

export const HINT = {
  uploads: 'E-Mails hochladen, um sie hier zu verarbeiten.',
  archive: 'Tickets auswählen, um sie zu archivieren.',
  restore: 'Tickets auswählen, um sie wiederherzustellen.',
  maintenance: 'Wartungsarbeiten — Aktionen pausiert',
} as const;

// ─── The pages ──────────────────────────────────────────────────────────────────────────────────
export function uploadsAutoAction(i: { selecting: boolean; picked: number; operable: number }): AutoAction {
  if (i.selecting) return pick('lightning', 'Verarbeiten', i.picked, i.picked > 0 ? pickedUploads(i.picked) : '0 ausgewählte Uploads');
  if (i.operable > 0) return rule('lightning', 'Verarbeiten', allUploads(i.operable));
  return idle('lightning', HINT.uploads);
}

export interface ExcelAllInput {
  /** describeExcelBatchAction's verdict for the empty-pick (view) gesture. */
  canAct: boolean;
  refusal: string | null;
  formBearingCount: number;
  countsLoading: boolean;
}

export function antwortenAutoAction(i: {
  purpose: 'archive' | 'excel' | null;
  picked: number;
  tab: 'offen' | 'entschieden';
  eligible: number;
  days: number;
  excel: ExcelAllInput;
}): AutoAction {
  if (i.purpose === 'excel') {
    if (i.picked > 0) return pick('download', 'Herunterladen', i.picked, pickedForms(i.picked));
    if (i.excel.canAct) return rule('mass-download', 'Herunterladen', i.excel.countsLoading ? VIEW_FORMS : allForms(i.excel.formBearingCount));
    return pick('download', 'Herunterladen', 0, pickedForms(0), i.excel.refusal);
  }
  if (i.purpose === 'archive') return pick('archive', 'Archivieren', i.picked, pickedTickets(i.picked));
  if (i.days > 0 && i.eligible > 0) {
    return rule('archive', 'Archivieren', i.tab === 'offen' ? olderThan(i.eligible, i.days) : decidedOver(i.eligible, i.days));
  }
  return idle('archive', HINT.archive);
}

/** The Archiv acts on a pick only — an empty selection means nothing here, on both levers. */
export function archivAutoAction(i: { purpose: 'archive' | 'excel' | null; picked: number }): AutoAction {
  if (i.purpose === 'excel') return pick('download', 'Herunterladen', i.picked, pickedForms(i.picked));
  if (i.purpose === 'archive') return pick('restore', 'Wiederherstellen', i.picked, pickedTickets(i.picked));
  return idle('restore', HINT.restore);
}

/** Maintenance pauses every write: no line, the quiet button, the hover says why. */
export const maintenanceAutoAction = (a: AutoAction): AutoAction => idle(a.icon, HINT.maintenance);
